
import { SEO } from "@/components/SEO";
import { Hero } from "@/components/Hero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card, CardContent } from "@/components/ui/card";
import { ServiceContactForm } from "@/components/service/ServiceContactForm";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Contractor } from "@/types/contractor";
import { ContractorsList } from "@/components/ContractorsList";
import { LoadingState } from "@/components/LoadingState";

const HouseCleaningPage = () => {
  const serviceType = "House Cleaning";
  
  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ['contractors', 'house-cleaning'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contractors')
        .select('*')
        .eq('service_type', 'House Cleaning')
        .eq('location', 'oklahoma-city')
        .order('featured', { ascending: false })
        .order('rating', { ascending: false });

      if (error) {
        console.error('Error fetching contractors:', error);
        throw error;
      }

      return data as Contractor[];
    },
  });

  const breadcrumbItems = [
    { label: "Services", href: "/services" },
    { label: "House Cleaning", href: "/house-cleaning-services-oklahoma-city" },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "House Cleaning Services in Oklahoma City",
    "description": "Connect with professional house cleaning services in Oklahoma City for residential and commercial cleaning.",
    "provider": {
      "@type": "Organization",
      "name": "Home Pros OKC"
    },
    "areaServed": {
      "@type": "City",
      "name": "Oklahoma City"
    },
    "serviceType": "House Cleaning"
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="House Cleaning Services in Oklahoma City | Home Pros OKC"
        description="Find reliable, professional house cleaning services in Oklahoma City for your home or business. Get free quotes from top cleaning pros."
        canonicalUrl="/house-cleaning-services-oklahoma-city"
        schema={schema}
      />

      <div className="container mx-auto px-4 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <Hero
          title="House Cleaning Services in Oklahoma City"
          subtitle="Find trusted, professional cleaning services for your home or business"
          buttonText="Get Free Quotes"
          buttonLink="#contact-form"
        />

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Professional Cleaning Services You Can Trust</h2>
          <p className="text-lg mb-8">
            Whether you need regular house cleaning, a deep clean, or specialized cleaning services, 
            our network of professional cleaners in Oklahoma City delivers spotless results every time.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Experienced Professionals</h3>
                <p>Our cleaning professionals have the skills and experience to tackle any cleaning challenge effectively and efficiently.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Eco-Friendly Options</h3>
                <p>Many of our cleaners offer green cleaning solutions that are safe for your family, pets, and the environment.</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Customized Cleaning Plans</h3>
                <p>Get personalized cleaning services tailored to your specific needs, schedule, and budget.</p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Our House Cleaning Services</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Residential Cleaning</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Regular maintenance cleaning</li>
                  <li>Deep cleaning services</li>
                  <li>Move-in/move-out cleaning</li>
                  <li>Spring cleaning</li>
                  <li>Post-renovation cleaning</li>
                  <li>Window and blind cleaning</li>
                </ul>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-2">Commercial Cleaning</h3>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Office cleaning services</li>
                  <li>Retail space cleaning</li>
                  <li>Medical facility cleaning</li>
                  <li>Restaurant and food service cleaning</li>
                  <li>Floor care and maintenance</li>
                  <li>Restroom sanitization</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="my-12 p-6 bg-muted rounded-lg">
          <h2 className="text-2xl font-bold mb-4">Benefits of Professional House Cleaning</h2>
          <p className="mb-4">
            Here's why hiring professional house cleaning services makes sense:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <span className="font-semibold">Time Savings:</span> Reclaim your free time by outsourcing cleaning tasks to professionals.
            </li>
            <li>
              <span className="font-semibold">Consistent Results:</span> Enjoy a consistently clean home or business without the hassle.
            </li>
            <li>
              <span className="font-semibold">Deep Cleaning Expertise:</span> Professionals have the tools and knowledge to clean areas you might miss.
            </li>
            <li>
              <span className="font-semibold">Healthier Environment:</span> Regular professional cleaning reduces allergens, dust, and germs.
            </li>
            <li>
              <span className="font-semibold">Customized Service:</span> Cleaning plans tailored to your specific needs and preferences.
            </li>
          </ul>
        </div>

        <div className="my-12">
          <h2 className="text-2xl font-bold mb-6">Ready for a Cleaner Space?</h2>
          <p className="text-lg mb-6">
            Connect with top-rated house cleaning professionals in Oklahoma City today. Fill out our simple form below to receive free, no-obligation quotes.
          </p>
          
          <ServiceContactForm 
            title="Request a Free Quote"
            description="Tell us about your cleaning needs, and we'll connect you with the best cleaning professionals in Oklahoma City."
          />
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-bold mb-6">Top House Cleaning Professionals in Oklahoma City</h2>
          
          {isLoading ? (
            <LoadingState message="Loading house cleaning professionals..." />
          ) : contractors && contractors.length > 0 ? (
            <ContractorsList
              contractors={contractors}
              isLoading={isLoading}
              viewMode="grid"
              onViewModeChange={() => {}}
              onServiceTypeChange={() => {}}
              onLocationChange={() => {}}
              selectedServiceType="House Cleaning"
            />
          ) : (
            <p className="text-center text-muted-foreground py-8">
              We're currently updating our list of house cleaning professionals in Oklahoma City. 
              Please check back soon or use the form above to request a quote.
            </p>
          )}
        </div>

        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold mb-4">Looking for Our Top-Rated Cleaning Services?</h3>
          <p className="mb-6">
            View our curated list of the best house cleaning services in Oklahoma City.
          </p>
          <a 
            href="/best-house-cleaning-oklahoma-city" 
            className="inline-block px-6 py-3 bg-primary text-white rounded-md hover:bg-primary/90 transition"
          >
            See Top-Rated Cleaning Services
          </a>
        </div>
      </div>
    </div>
  );
};

export default HouseCleaningPage;
